/**
 * Pure logic of the products screen and its dialogs: picture URLs, size display
 * of a line, central stock quantity, store colours by stock.
 */
import type { Store } from '@/shared/config/app-config';
import { getImage, STORAGE } from '@/shared/lib/image';
import type { ProductLine } from '@/shared/config/product-line';
import type { Product, ProductInput, ProductSize, Stock } from './api';

export const SIZE_DISPLAYS = ['noSize', 'size', 'shoeSize'] as const;
export type SizeDisplay = (typeof SIZE_DISPLAYS)[number];

const isSizeDisplay = (value: unknown): value is SizeDisplay =>
  SIZE_DISPLAYS.some((display) => display === value);

/** Signed URL of a product picture (`main`, `custom`, `custom2`, `custom3`). */
export const productPictureUrl = (
  id: string | null | undefined,
  type: string,
): string | null =>
  id
    ? getImage('abc', STORAGE.AbcDiorBack, `/product-pictures/${id}-${type}`)
    : null;

/** What the products screen reads of a product line. */
export type ProductLineSummary = Pick<
  ProductLine,
  'id' | 'productLine' | 'sizeDisplay' | 'stores'
>;

/** Size display of the product line a product belongs to, `noSize` when unknown. */
export const sizeDisplayOf = (
  lines: readonly ProductLineSummary[],
  line: string | null | undefined,
): SizeDisplay => {
  const found = lines.find((entry) => entry.productLine === line);
  return isSizeDisplay(found?.sizeDisplay) ? found.sizeDisplay : 'noSize';
};

const joinCodes = (
  sizes: readonly ProductSize[],
  key: 'ean13Stock' | 'ean13CentralFlow' | 'm3SkuStock' | 'm3SkuCentralFlow',
): string =>
  sizes
    .map((size) => size[key])
    .filter((code): code is string => !!code)
    .join(',');

/** EAN and SKU lists sent to the stock API for the given flow. */
export const stockCodes = (
  sizes: readonly ProductSize[],
  flow: 'stock' | 'centralFlow',
): { ean: string; sku: string } =>
  flow === 'stock'
    ? {
        ean: joinCodes(sizes, 'ean13Stock'),
        sku: joinCodes(sizes, 'm3SkuStock'),
      }
    : {
        ean: joinCodes(sizes, 'ean13CentralFlow'),
        sku: joinCodes(sizes, 'm3SkuCentralFlow'),
      };

/** "M3 quantity": the central stock of every size, matched on EAN and SKU. */
export const centralStockQuantity = (
  sizes: readonly ProductSize[],
  stocks: readonly Stock[],
): number =>
  sizes.reduce((total, size) => {
    const stock = stocks.find(
      (entry) =>
        size.ean13CentralFlow === entry.ean &&
        size.m3SkuCentralFlow === entry.sku,
    );
    return stock ? total + (stock.quantity ?? 0) : total;
  }, 0);

export const STORE_COLORS = {
  black: 'rgba(0,0,0,0.87)',
  red: 'rgba(220,0,0,0.87)',
  green: 'rgba(0,160,0,0.87)',
  orange: 'rgba(240,150,0,0.87)',
  grey: 'rgba(170,170,170,0.87)',
} as const;

interface StoreWithStock extends Store {
  storeCode?: string | null;
  stockControl?: boolean | null;
}

export interface StoreColorContext {
  stores: readonly Store[];
  sizes: readonly ProductSize[];
  stocks: readonly Stock[] | null;
  stocksActive: boolean;
  stockControl: boolean;
}

/**
 * Colour of a row of the store tree from the product's stock: black when stock control is off or unknown, grey
 * without information, green / orange / red by availability.
 */
export const storeColor = (
  context: StoreColorContext,
  node: { level: number; label: string; storeId?: string | undefined },
): string => {
  const { black, red, green, orange, grey } = STORE_COLORS;
  if (!context.stocksActive || !context.stockControl || !context.stocks)
    return black;
  const list = context.stores as readonly StoreWithStock[];
  const stocks = context.stocks.filter((stock) =>
    context.sizes.some(
      (size) => size.ean13Stock === stock.ean && size.m3SkuStock === stock.sku,
    ),
  );
  const quantityFor = (store: StoreWithStock): number | null => {
    if (!store.stockControl) return null;
    const ofStore = stocks.filter(
      (stock) =>
        String(stock.storeCode).toUpperCase() ===
        String(store.storeCode).toUpperCase(),
    );
    if (!ofStore.length) return null;
    return ofStore.reduce((sum, stock) => sum + (stock.quantity ?? 0), 0);
  };

  if (node.level === 0 || node.level === 1) {
    const zone = list
      .filter(
        (store) =>
          (node.level === 0 ? store.region : store.country) === node.label,
      )
      .filter((store) => store.stockControl);
    if (!zone.length) return black;
    const quantities = zone.map(quantityFor);
    if (quantities.every((quantity) => quantity == null)) return grey;
    if (quantities.every((quantity) => quantity == null || quantity > 0))
      return green;
    if (quantities.some((quantity) => quantity != null && quantity > 0))
      return orange;
    return red;
  }
  const store = list.find((entry) => String(entry.id) === node.storeId);
  if (!store?.stockControl) return black;
  const quantity = quantityFor(store);
  if (quantity == null) return grey;
  return quantity > 0 ? green : red;
};

/** Rows of the read-only store tree of the product dialog: the stores of its line. */
export const productStoreIds = (
  lines: readonly ProductLineSummary[],
  line: string | null | undefined,
): string[] => lines.find((entry) => entry.productLine === line)?.stores ?? [];

export interface ProductFormValues {
  reference: string;
  name: string;
  line: string;
  workshop: string;
  centralStockOnly: boolean;
  stockControl: boolean;
  centralStockControl: boolean;
  displayOrder: string;
}

export type ProductFormErrors = Partial<
  Record<'reference' | 'name' | 'line' | 'displayOrder', string>
>;

export const emptyProductForm = (): ProductFormValues => ({
  reference: '',
  name: '',
  line: '',
  workshop: '',
  centralStockOnly: false,
  stockControl: false,
  centralStockControl: false,
  displayOrder: '',
});

export const productToForm = (product: Product): ProductFormValues => ({
  reference: product.reference ?? '',
  name: product.name ?? '',
  line: product.line ?? '',
  workshop: product.workshopId ?? '',
  centralStockOnly: product.centralStockOnly ?? false,
  stockControl: product.stockControl ?? false,
  centralStockControl: product.centralStockControl ?? false,
  displayOrder:
    product.displayOrder == null ? '' : String(product.displayOrder),
});

const DISPLAY_ORDER_PATTERN = /^\d{1,5}$/;

export const validateProductForm = (
  values: ProductFormValues,
): ProductFormErrors => {
  const errors: ProductFormErrors = {};
  if (values.reference.length > 100)
    errors.reference = 'Wrong commercial reference';
  if (!values.name || values.name.length > 100)
    errors.name = 'Wrong product name';
  if (!values.line) errors.line = 'Wrong line';
  if (!values.displayOrder || !DISPLAY_ORDER_PATTERN.test(values.displayOrder))
    errors.displayOrder = 'Wrong display order';
  return errors;
};

/** Payload of the create / update calls. */
export const formToProduct = (
  values: ProductFormValues,
  stores: readonly string[],
  now: number,
): ProductInput => ({
  reference: values.reference,
  name: values.name,
  displayOrder: values.displayOrder,
  line: values.line,
  stockControl: values.stockControl,
  centralStockControl: values.centralStockControl,
  centralStockOnly: values.centralStockOnly,
  mainPictureUpdatedAt: now,
  customPictureUpdatedAt: now,
  custom2PictureUpdatedAt: now,
  custom3PictureUpdatedAt: now,
  stores: [...stores],
  workshopId: values.workshop || null,
});

/** Labels of the custom picture fields by size display. */
export const customPictureLabels = (
  sizeDisplay: SizeDisplay,
): { type: 'custom' | 'custom2' | 'custom3'; label: string }[] => {
  switch (sizeDisplay) {
    case 'size':
      return [
        { type: 'custom', label: 'Custom picture 1 / customization' },
        { type: 'custom2', label: 'Custom picture 2 / zoom view' },
      ];
    case 'shoeSize':
      return [
        { type: 'custom', label: 'Custom picture 1 / left shoe' },
        { type: 'custom2', label: 'Custom picture 2 / right shoe' },
        { type: 'custom3', label: 'Custom picture 3 / above view' },
      ];
    default:
      return [{ type: 'custom', label: 'Custom picture' }];
  }
};
